'use client';

import React, { useCallback, useMemo, useState } from 'react';

import { CountDownTrivias } from './CountDownTrivias';

import { Button } from '@/components';
import type {
  ResponseDashTriviaInit,
  ResponsePreguntaTrivia,
} from '@/types/dashboard';
import { ABCD } from '@/utils';

interface Props {
  handleNextQuestion: () => Promise<void>;
  currentQuestion: ResponsePreguntaTrivia | null;
  currentAnswer: number | undefined;
  triviaData: ResponseDashTriviaInit | undefined;
  handleAnswer: (answer: number) => Promise<null | undefined>;
  isAnswered: (index: number) => void;
  correctAnswer: number | undefined;
  isMobile?: boolean;
}

export type TriviaStateQuestions =
  | 'idle'
  | 'answering'
  | 'checking'
  | 'completed';

const defaultStylesQuestion = (mobile = false) => {
  return {
    parent: `border-gdt-blue-light2 ${mobile && 'bg-white'}`,
    text: 'text-gdt-blue',
    icon: 'bg-gdt-blue-light2 text-gdt-blue',
    mark: null,
  };
};

const getCorrectStylesQuestion = () => ({
  parent: 'border-gdt-green bg-gdt-status-green-light',
  text: 'text-gdt-status-green',
  icon: 'bg-gdt-status-green text-white',
  mark: '/assets/images/ic_check.svg',
});

const getIncorrectStylesQuestion = () => ({
  parent: 'border-gdt-red bg-gdt-status-red-light',
  text: 'text-gdt-status-red',
  icon: 'bg-gdt-status-red text-white',
  mark: '/assets/images/ic_x.svg',
});

const setAnswerTriviaClasses = (
  answer: number,
  currentAnswer?: number,
  correctAnswer?: number,
  mobile?: boolean,
  state: TriviaStateQuestions = 'idle'
) => {
  const isSelected = answer === currentAnswer;
  const isCorrect = answer === correctAnswer;

  // Si esta respuesta fue seleccionada
  if (isSelected) {
    switch (state) {
      case 'checking':
        return defaultStylesQuestion();
      case 'completed':
        return currentAnswer === correctAnswer
          ? getCorrectStylesQuestion()
          : getIncorrectStylesQuestion();
      default:
        return defaultStylesQuestion(mobile);
    }
  }

  // Mostrar respuesta correcta solo cuando el estado esté completado
  if (state === 'completed' && isCorrect && currentAnswer !== correctAnswer) {
    return getCorrectStylesQuestion();
  }

  return defaultStylesQuestion(mobile);
};

export const Questions = ({
  handleNextQuestion,
  currentQuestion,
  currentAnswer,
  triviaData,
  handleAnswer,
  isAnswered,
  correctAnswer,
  isMobile = false,
}: Props) => {
  const [answerState, setAnswerState] = useState<TriviaStateQuestions>('idle');

  const progressIndicators = useMemo(
    () => (
      <div className={`flex gap-2 ${isMobile ? 'justify-center w-full' : ''}`}>
        {[...Array(currentQuestion?.totalPreguntas).keys()].map((_, index) => (
          <div
            key={_}
            className={`w-2 h-2 rounded-[3px] ${isAnswered(index)}`}
          />
        ))}
      </div>
    ),
    [currentQuestion?.totalPreguntas, isAnswered, isMobile]
  );

  const onHandleAnswer = useCallback(
    async (option: number) => {
      if (currentAnswer) return;
      setAnswerState('checking');
      try {
        await handleAnswer(option);
        setAnswerState('completed');
      } catch (error) {
        setAnswerState('idle');
      }
    },
    [currentAnswer, handleAnswer]
  );

  const RenderOptions = useCallback(
    () => (
      <div className="flex flex-col gap-y-3 mt-3">
        {currentQuestion?.opciones.map((option, index) => (
          <button
            key={option.nroOpcion}
            className={`flex item-center px-4 py-2 rounded-lg border cursor-pointer ${
              setAnswerTriviaClasses(
                option.nroOpcion,
                currentAnswer,
                correctAnswer,
                isMobile,
                answerState
              ).parent
            }`}
            onClick={() => onHandleAnswer(option.nroOpcion)}
          >
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center font-semibold ${
                setAnswerTriviaClasses(
                  option.nroOpcion,
                  currentAnswer,
                  correctAnswer,
                  isMobile,
                  answerState
                ).icon
              }`}
            >
              {ABCD[index]}
            </div>
            <span
              className={`ml-2 font-semibold ${
                setAnswerTriviaClasses(
                  option.nroOpcion,
                  currentAnswer,
                  correctAnswer,
                  isMobile,
                  answerState
                ).text
              }`}
            >
              {option.consigna}
            </span>
            {setAnswerTriviaClasses(
              option.nroOpcion,
              currentAnswer,
              correctAnswer,
              isMobile,
              answerState
            )?.mark && (
              <img
                alt="Mark"
                className="ml-auto my-auto"
                src={
                  setAnswerTriviaClasses(
                    option.nroOpcion,
                    currentAnswer,
                    correctAnswer,
                    isMobile,
                    answerState
                  ).mark ?? undefined
                }
              />
            )}
          </button>
        ))}
      </div>
    ),
    [
      currentQuestion?.opciones,
      currentAnswer,
      correctAnswer,
      isMobile,
      answerState,
      onHandleAnswer,
    ]
  );

  const RenderContent = useCallback(() => {
    const paddingClass = isMobile ? 'px-5' : 'px-12';
    const questionClass = isMobile ? 'font-semibold' : '';

    return (
      <div className={`${paddingClass} pt-4`}>
        <p className={questionClass}>{currentQuestion?.consigna}</p>
        <RenderOptions />
      </div>
    );
  }, [RenderOptions, currentQuestion?.consigna, isMobile]);

  const RenderButton = useCallback(() => {
    if (!currentAnswer) return null;

    const buttonClass = isMobile ? 'w-full' : '';
    const containerClass = isMobile ? 'px-5' : '';

    return (
      <div className={`flex justify-center py-4 ${containerClass}`}>
        <Button
          className={buttonClass}
          type="button"
          variant="primary"
          onClick={handleNextQuestion}
        >
          SIGUIENTE
        </Button>
      </div>
    );
  }, [currentAnswer, handleNextQuestion, isMobile]);

  if (isMobile) {
    return (
      <div className="flex flex-col">
        <div className="flex flex-col gap-y-4">
          <CountDownTrivias isMobile={true} triviaData={triviaData}>
            {progressIndicators}
          </CountDownTrivias>
          <RenderContent />
          <RenderButton />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col">
      <div className="flex flex-col">
        <CountDownTrivias triviaData={triviaData}>
          {progressIndicators}
        </CountDownTrivias>
        <RenderContent />
        <RenderButton />
      </div>
    </div>
  );
};

// Componentes de conveniencia para mantener compatibilidad
export const QuestionsDesktop = React.memo((props: Omit<Props, 'isMobile'>) => (
  <Questions {...props} isMobile={false} />
));

export const QuestionsMobile = React.memo((props: Omit<Props, 'isMobile'>) => (
  <Questions {...props} isMobile={true} />
));

export default Questions;
